import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, useTheme, useMediaQuery, Tooltip, Chip } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Sector, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Area, AreaChart, LineChart, Line } from 'recharts';
type ChartType = 'donut' | 'radar' | 'area' | 'line' | 'progress' | 'stackedBar';
interface ChartCardProps {
  title: string;
  type: ChartType;
  data?: any;
  height?: number;
}
const ChartCard: React.FC<ChartCardProps> = ({
  title,
  type,
  data = null,
  height = 240
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeIndex, setActiveIndex] = useState(0);
  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };
  const renderDonutChart = () => {
    if (!data) return null;
    const chartData = data.labels.map((label: string, index: number) => ({
      name: label,
      value: data.values[index],
      count: data.counts[index]
    }));
    const renderActiveShape = (props: any) => {
      const {
        cx,
        cy,
        innerRadius,
        outerRadius,
        startAngle,
        endAngle,
        fill,
        payload
      } = props;
      return <g data-id="element-392" data-mp="49u3k2zx7ojm">
          <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill={fill} fontSize={14} fontWeight="bold" data-id="element-393" data-mp="4alyrcplvk9d">{payload.name}</text>
          <text x={cx} y={cy + 15} textAnchor="middle" fill="#888" fontSize={13} data-id="element-394" data-mp="32t0yfqu3q4xl">{payload.count?.toLocaleString()}</text>
          <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 5} startAngle={startAngle} endAngle={endAngle} fill={fill} data-id="element-395" data-mp="37r2hibrj2492" />
        </g>;
    };
    return <Box sx={{
      height,
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }} data-id="element-396" data-mp="3hhdxwxsee5wu">
        <ResponsiveContainer width="100%" height="100%" data-id="element-397" data-mp="2k06vxwdo05a9">
          <PieChart data-id="element-398" data-mp="2p0bdzqu8182l">
            <Pie activeIndex={activeIndex} activeShape={renderActiveShape} data={chartData} cx="50%" cy="45%" innerRadius={isMobile ? 45 : 60} outerRadius={isMobile ? 65 : 80} dataKey="value" onMouseEnter={onPieEnter} paddingAngle={2} data-id="element-399" data-mp="2ozrs5z1g9zja">
              {chartData.map((entry: any, index: number) => <Cell key={`cell-${index}`} fill={data.colors[index]} stroke={data.colors[index]} strokeWidth={1} data-id="element-400" data-mp="2qpf2a9er0f6q" data-mpi={index} />)}
            </Pie>
            <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{
            paddingTop: 12,
            fontSize: 13
          }} formatter={(value: string, entry: any, index: number) => <span style={{
            color: data.colors[index],
            fontWeight: activeIndex === index ? 'bold' : 'normal',
            fontSize: '0.8rem'
          }} data-id="element-402" data-mp="1tzfnhx808kfp">{value} ({data.values[index]}%)</span>} data-id="element-401" data-mp="2lrdan039b7jn" />
          </PieChart>
        </ResponsiveContainer>
      </Box>;
  };
  const renderStackedBarChart = () => {
    if (!data) return null;
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-403" data-mp="3jdayze4bi73i">
        <ResponsiveContainer width="100%" height="100%" data-id="element-404" data-mp="3jg6xp4nzozt0">
          <BarChart data={data.categories.map((category: string, index: number) => {
          const entry: any = {
            name: category
          };
          data.series.forEach((s: any) => {
            entry[s.name] = s.data[index];
          });
          return entry;
        })} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5
        }} data-id="element-405" data-mp="3edy1lzftlhmo">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" data-id="element-406" data-mp="r6jwwtiff76d" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-407" data-mp="r8wkpnav8brc" />
            <YAxis axisLine={false} tickLine={false} data-id="element-408" data-mp="w66ibq8ertq9" />
            <RechartsTooltip formatter={(value, name) => [`${value}`, name]} labelFormatter={label => `Day: ${label}`} contentStyle={{
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: 'none'
          }} data-id="element-409" data-mp="1jupfpapx6m3k" />
            <Legend wrapperStyle={{
            paddingTop: 15
          }} formatter={value => <span style={{
            fontSize: 14,
            color: '#333'
          }} data-id="element-411" data-mp="1f4iulkum9jq2">{value}</span>} data-id="element-410" data-mp="1a67guz17ap2k" />
            {data.series.map((s: any, index: number) => <Bar key={s.name} dataKey={s.name} stackId="a" fill={s.color} radius={[index === 0 && 4, index === 0 && 4, index === data.series.length - 1 && 4, index === data.series.length - 1 && 4]} data-id="element-412" data-mp="22tjtohhk7iio" data-mpi={index} />)}
          </BarChart>
        </ResponsiveContainer>
      </Box>;
  };
  const renderAreaChart = () => {
    if (!data) return null;
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-413" data-mp="dyco6seb4cm3">
        <ResponsiveContainer width="100%" height="100%" data-id="element-414" data-mp="9y10f6qndw6e">
          <AreaChart data={data.categories.map((category: string, index: number) => {
          const entry: any = {
            name: category
          };
          data.series.forEach((s: any) => {
            entry[s.name] = s.data[index];
          });
          return entry;
        })} margin={{
          top: 10,
          right: 30,
          left: 0,
          bottom: 0
        }} data-id="element-415" data-mp="9ykm8yjf54pp">
            <defs data-id="element-416" data-mp="xoy3du0gtj5i"><linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-417" data-mp="xoy45w8ahn6k"><stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.8} data-id="element-418" data-mp="sx6jg2bteboc" /><stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.1} data-id="element-419" data-mp="svlsemoplav8" /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" data-id="element-420" data-mp="nxk9c1ra9bjr" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-421" data-mp="1leiwh5qc1wsi" />
            <YAxis axisLine={false} tickLine={false} data-id="element-422" data-mp="1gm7nzehj12oc" />
            <RechartsTooltip formatter={(value, name) => [`${value}`, name]} contentStyle={{
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: 'none'
          }} data-id="element-423" data-mp="1glg4h217evef" />
            {data.series.map((s: any) => <Area key={s.name} type="monotone" dataKey={s.name} stroke="#0EA5E9" strokeWidth={3} fillOpacity={1} fill="url(#colorGradient)" data-id="element-424" data-mp="1sz30o9ab3swj" />)}
          </AreaChart>
        </ResponsiveContainer>
      </Box>;
  };
  const renderLineChart = () => {
    if (!data) return null;
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-425" data-mp="26w8c6osshjz6">
        <ResponsiveContainer width="100%" height="100%" data-id="element-426" data-mp="2bvl0ldzb6z3v">
          <LineChart data={data.categories.map((category: string, index: number) => {
          const entry: any = {
            name: category
          };
          data.series.forEach((s: any) => {
            entry[s.name] = s.data[index];
          });
          return entry;
        })} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5
        }} data-id="element-427" data-mp="2bu06tqktjk72">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" data-id="element-428" data-mp="2poanrlqkqa2k" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-429" data-mp="2ult07m3sv7fb" />
            <YAxis axisLine={false} tickLine={false} data-id="element-430" data-mp="2ujzovti8rz0z" />
            <RechartsTooltip formatter={(value, name) => [`${value}`, name]} contentStyle={{
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: 'none'
          }} data-id="element-431" data-mp="ukw5jpjl36fv" />
            <Legend data-id="element-432" data-mp="14afmgpnqkq83" />
            {data.series.map((s: any) => <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={3} dot={{
            r: 4,
            strokeWidth: 2
          }} activeDot={{
            r: 6,
            strokeWidth: 0
          }} data-id="element-433" data-mp="14bz6mrcijlf5" />)}
          </LineChart>
        </ResponsiveContainer>
      </Box>;
  };
  const renderProgressChart = () => <Box sx={{
    height,
    p: 2
  }} data-id="element-434" data-mp="197xrw62jonbc"><Box sx={{
      mb: 4
    }} data-id="element-435" data-mp="198pmpeobih0k"><Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 1
      }} data-id="element-436" data-mp="19bbqd3838cdi"><Typography variant="body2" data-id="element-437" data-mp="1n88hz6w15xvg">KYC Completed</Typography><Typography variant="body2" fontWeight="600" color="primary" data-id="element-438" data-mp="1i9x9p0ldvvfc">78%</Typography></Box><LinearProgress variant="determinate" value={78} sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: '#E0F7FF',
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
          backgroundColor: '#0EA5E9'
        }
      }} data-id="element-439" data-mp="3p0541aftqq4h" /></Box><Box sx={{
      mb: 4
    }} data-id="element-440" data-mp="1zizrxn2ndv4y"><Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 1
      }} data-id="element-441" data-mp="1zihmq0y0c6kw"><Typography variant="body2" data-id="element-442" data-mp="1zlc75sh0treb">Cases in Review</Typography><Typography variant="body2" fontWeight="600" color="primary" data-id="element-443" data-mp="24h1b0802pugf">23%</Typography></Box><LinearProgress variant="determinate" value={23} sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: '#E0F7FF',
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
          backgroundColor: '#0EA5E9'
        }
      }} data-id="element-444" data-mp="1qr7j1gt9uu2h" /></Box><Box data-id="element-445" data-mp="1gryux4l389o1"><Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 1
      }} data-id="element-446" data-mp="1gs8m2vhvcmvb"><Typography variant="body2" data-id="element-447" data-mp="1lrl7rcx07nwb">Rejected Cases</Typography><Typography variant="body2" fontWeight="600" color="error" data-id="element-448" data-mp="1lse6bqlt773d">3%</Typography></Box><LinearProgress variant="determinate" value={3} sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: '#FFEBEB',
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
          backgroundColor: '#DC2626'
        }
      }} data-id="element-449" data-mp="17sllud0vln3u" /></Box></Box>;
  const renderRadarChart = () => <Box sx={{
    height,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  }} data-id="element-450" data-mp="326mfmjhkjjx2"><svg width="200" height="200" viewBox="0 0 200 200" data-id="element-451" data-mp="365c2r3sesftw"><polygon points="100,10 190,78 154,190 46,190 10,78" fill="rgba(14, 165, 233, 0.1)" stroke="#0EA5E9" strokeWidth="2" data-id="element-452" data-mp="1tycohu3tdvid" /><polygon points="100,40 160,88 134,160 66,160 40,88" fill="rgba(14, 165, 233, 0.2)" stroke="#0EA5E9" strokeWidth="2" data-id="element-453" data-mp="1p0b2p1f07i37" /></svg><Box sx={{
      position: 'absolute',
      bottom: 10,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      gap: 2
    }} data-id="element-454" data-mp="1k6gky3iyld6r"><Typography variant="caption" data-id="element-455" data-mp="1f7cevlrbzvkn">Coverage: 87%</Typography></Box></Box>;
  const renderChart = () => {
    switch (type) {
      case 'donut':
        return renderDonutChart();
      case 'stackedBar':
        return renderStackedBarChart();
      case 'area':
        return renderAreaChart();
      case 'line':
        return renderLineChart();
      case 'radar':
        return renderRadarChart();
      case 'progress':
        return renderProgressChart();
      default:
        return null;
    }
  };
  return <Card sx={{
    height: '100%',
    width: '100%',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
  }} data-id="element-456" data-mp="2vm5cl875r0dw"><CardContent sx={{
      p: 3,
      height: '100%',
      width: '100%',
      boxSizing: 'border-box'
    }} data-id="element-457" data-mp="2qovc8xi8d4ba"><Typography variant="h6" component="h2" gutterBottom sx={{
        fontWeight: 600,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-458" data-mp="2z4b2m4yeqkwz">{title}</Typography>{renderChart()}</CardContent></Card>;
};
export default ChartCard;